import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ResetPassword() {
  const [, navigate] = useLocation();
  const { session, clearMustChangePassword } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const changeMutation = trpc.clientAuth.changePassword.useMutation({
    onSuccess: () => {
      clearMustChangePassword();
      setSuccess(true);
      setTimeout(() => navigate("/"), 2000);
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("A senha deve ter pelo menos 8 caracteres");
      return;
    }
    if (newPassword !== confirm) {
      setError("As senhas não coincidem");
      return;
    }
    if (!session?.token) {
      setError("Sessão inválida — faça login novamente");
      navigate("/login");
      return;
    }
    changeMutation.mutate({ sessionToken: session.token, newPassword });
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--solar-bg)] p-4">
        <div className="text-center max-w-sm w-full rounded-xl border border-[var(--solar-line)] bg-white shadow-[var(--elev-2)] p-10">
          <ShieldCheck className="h-16 w-16 text-[var(--brand)] mx-auto mb-4" />
          <h2 className="text-[var(--solar-ink)] text-2xl font-bold">Senha redefinida com sucesso</h2>
          <p className="text-[var(--ink-2)] mt-2">Redirecionando para a plataforma...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--solar-bg)] p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <ShieldCheck className="h-12 w-12 text-[var(--brand)] mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-[var(--solar-ink)]">Defina sua nova senha</h1>
          <p className="text-[var(--solar-mute)] text-sm mt-1">
            Por segurança, você precisa criar uma nova senha antes de continuar
          </p>
        </div>

        <Card className="bg-white border-[var(--solar-line)] shadow-[var(--elev-2)] rounded-xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-[var(--solar-ink)] text-lg">Nova senha</CardTitle>
            <CardDescription className="text-[var(--solar-mute)]">
              Mínimo de 8 caracteres. Use letras, números e símbolos para maior segurança.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4 bg-red-50 border-red-200">
                <AlertDescription className="text-[var(--destructive)]">{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-wide font-semibold text-[var(--solar-mute)]">Nova senha</Label>
                <div className="relative">
                  <Input
                    type={showPw ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-white border-[var(--solar-line)] text-[var(--solar-ink)] placeholder:text-[var(--solar-mute)] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--solar-mute)] hover:text-[var(--solar-ink)]"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-wide font-semibold text-[var(--solar-mute)]">Confirmar senha</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-white border-[var(--solar-line)] text-[var(--solar-ink)] placeholder:text-[var(--solar-mute)]"
                />
              </div>

              {/* Password strength indicator */}
              {newPassword.length > 0 && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          newPassword.length >= level * 3
                            ? "bg-[var(--brand)]"
                            : "bg-[var(--solar-line)]"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-[var(--solar-mute)]">
                    {newPassword.length < 8 ? "Muito curta" : newPassword.length < 12 ? "Razoável" : newPassword.length < 16 ? "Boa" : "Excelente"}
                  </p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={changeMutation.isPending}
              >
                {changeMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvando...</>
                ) : "Salvar nova senha"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
